import React, { useContext, useEffect } from 'react';

import ArticleIcon from '@mui/icons-material/Article';
import {
    Box,
    Container as MaterialContainer,
    Grid,
    Tab,
    Tabs,
    TabPanel,
} from '@sunwisesoftware/sunwise-ui';
import { push } from 'connected-react-router';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import { HeaderContainer, TitleIcon } from 'common/components';
import CollapsedBreadcrumbs from 'common/components/CollapsedBreadcrumbs';
import { useScrollTop, useBreakpoint } from 'common/hooks';
import { LoadingContext } from 'common/utils/contexts';

import * as contactsActions from '../contacts/actions';
import createContactModal from '../createContactModal';
import * as createContactModalActions from '../createContactModal/actions';
import * as createProjectModalActions from '../createProjectModal/actions';
import KeyIndicators from '../project/components/KeyIndicatorsReports';
import projectsView from '../projectsView';

import * as actions from './actions';
import * as selectors from './selectors';

const Container = ({
    contact,
    fetchContact,
    //handleClickBulkItems,
    handleClickCreate,
    handleClickUpdate,
    isFetchingContact,
    match,
    redirect,
    selectedTab,
    selectTab,
}) => {
    const { t } = useTranslation();
    const loadingContext = useContext(LoadingContext);
    const breakpoint = useBreakpoint();
    const contactId = match.params.contactId;
    const projectId = match.params.projectId;

    const breadCrumbs = [
        {
            label: t('Contact', { count: 2 }),
            action: () => redirect(`/contacts`),
        },
    ];

    useScrollTop();

    useEffect(() => {
        fetchContact(contactId);
    }, [contactId]);

    useEffect(() => {
        if (isEmpty(loadingContext)) return;
        if (isFetchingContact)
            loadingContext.openLoading(t('Loading').concat('...'));
        else loadingContext.closeLoading();
    }, [isFetchingContact]);

    useEffect(() => {
        selectTab('projects');
    }, [breakpoint]);

    const { status_flag } = contact;
    const isArchived = status_flag === 'archived';

    const handleChange = (_, newValue) => selectTab(newValue);

    return (
        <MaterialContainer maxWidth={false}>
            <HeaderContainer sxDivider={{ mb: 0 }}>
                <Grid size="grow">
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        <ArticleIcon
                            fontSize="medium"
                            sx={{ color: 'primary.main' }}
                        />
                        <TitleIcon title={contact.name} />
                    </Box>
                    <CollapsedBreadcrumbs
                        current={contact.name}
                        locations={breadCrumbs}
                    />
                </Grid>
            </HeaderContainer>
            <Grid container>
                <Grid size={{ lg: 18, md: 18, xs: 18 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={selectedTab} onChange={handleChange}>
                            <Tab
                                key={`tab-projects`}
                                id={`tab-projects`}
                                label={t('Project', { count: 2 })}
                                value={'projects'}
                            />
                        </Tabs>
                    </Box>

                    <TabPanel
                        key={`tab-panel-projects`}
                        selectedTab={selectedTab}
                        value={'projects'}
                    >
                        <Grid container spacing={2}>
                            <Grid
                                size={{ md: 14, xs: 18 }}
                                sx={{ order: { md: 0, xs: 1 } }}
                            >
                                <projectsView.Container
                                    key={`projects-view-${contactId}`}
                                    breakpoint={breakpoint}
                                    contactId={contactId}
                                    contactIsArchived={isArchived}
                                    projectId={projectId}
                                    handleClickCreate={handleClickCreate}
                                />
                            </Grid>
                            <Grid
                                size={{ md: 4, xs: 18 }}
                                sx={{ order: { md: 1, xs: 0 } }}
                            >
                                <KeyIndicators
                                    contactDetails={contact}
                                    handleClickUpdate={handleClickUpdate}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Grid>
            </Grid>

            <createContactModal.Container
                onSuccessSave={() => fetchContact(contactId)}
            />
        </MaterialContainer>
    );
};

Container.propTypes = {
    contact: PropTypes.object,
    fetchContact: PropTypes.func,
    //handleClickBulkItems: PropTypes.func,
    handleClickCreate: PropTypes.func,
    handleClickUpdate: PropTypes.func,
    isFetchingContact: PropTypes.bool,
    match: PropTypes.object,
    redirect: PropTypes.func,
    selectedTab: PropTypes.string,
    selectTab: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    contact: selectors.getContactData,
    isFetchingContact: selectors.getIsFetchingContact,
    selectedTab: selectors.getSelectedTab,
});

const mapDispatchToProps = (dispatch) => ({
    fetchContact: (uid) => dispatch(actions.fetchContact(uid)),
    handleClickBreadcrumb: () => dispatch(actions.handleClickBreadcrumb()),
    handleClickBulkItems: (ids, value, callback) =>
        dispatch(contactsActions.prepareBulkItems(ids, value, {}, callback)),
    handleClickCreate: (contactId) =>
        dispatch(createProjectModalActions.prepareCreate(contactId)),
    handleClickUpdate: (values) =>
        dispatch(createContactModalActions.prepareUpdate(values)),
    redirect: (link) => dispatch(push(link)),
    selectTab: (tab) => dispatch(actions.selectTab(tab)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(Container));
